import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import styled from 'styled-components';
import { Field, Form, useField, useFormikContext } from 'formik';
import _, { identity, noop } from 'lodash';
import FormInput from '../basics/FormInput';
import DropdownButton from '../basics/DropdownButton';
import { N_YEARS } from '../../constants/years';
import { asArray, validateOnKeypress } from '../../utils/form';
import { useValueWidth } from '../../utils/hooks';
import {
  useFormatAndParseWithUnit,
  useUnits,
} from '../../contexts/UnitContext';
import { numberAsRoundedDecimal } from '../../utils/formatters';
import { setRef } from '../../utils/refs';
import { UncontrolledWhiteTooltip } from '../basics/WhiteTooltip';

export const SmallFontLargeFormattedInput = styled(FormInput)`
  font-size: 0.875rem;
`;

// An input that uses an uncontrolled component to avoid complications with
// format and parse while typing. If we used the stored parsed value
// as the controlled component value, it would format it as the
// user types, which leads to strange behavior.
// Note: the parse and format functions should be memoized. Updating them
// on every render may cause formatting issues.
const FormattedInput = ({
  value: formattedFormikStateValue,
  format,
  parse,
  inputRef,
  ...props
}) => {
  const localInputRef = useRef();
  useEffect(() => {
    const parsedValue = parse(localInputRef.current.value);
    const formattedParsedValue = format(parsedValue);
    const parsedFormikStateValue = parse(formattedFormikStateValue);
    if (
      localInputRef.current.value === '' ||
      formattedParsedValue !== formattedFormikStateValue ||
      parsedValue !== parsedFormikStateValue
    ) {
      localInputRef.current.value =
        formattedFormikStateValue == null ? '' : formattedFormikStateValue;
    }
  }, [formattedFormikStateValue, format, parse, localInputRef]);

  return (
    <SmallFontLargeFormattedInput
      innerRef={(el) => {
        // Set the local ref and the parent ref
        localInputRef.current = el;
        setRef(inputRef, el);
      }}
      {...props}
    />
  );
};

export const WideFormGroup = styled(FormGroup)`
  width: 100%;
  max-width: ${({ $maxWidth = '300px' }) => $maxWidth};
  input {
    width: 100%;
  }
`;

const YearInput = styled(FormattedInput)`
  &::placeholder {
    color: rgba(33, 33, 33, 0.2);
  }
  /* Hide input arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
  margin-right: 2px;
`;

export const YearInputs = ({
  // Providing a function for children allows you to render
  // the field with a custom layout. children will be called with the functions:
  // { renderInput, renderError, renderLabel }
  children,
  accessor,
  inputType = 'number',
  min,
  step,
  indexOffset = 0, // Adds indexOffset to determine the stored indices
  label,
  unitSpec,
  size,
  format = identity,
  parse = identity,
  ...props
}) => {
  const { touched: allTouched, errors } = useFormikContext();
  const { transformToUnit, transformToDefault, displayUnit } = useUnits(
    unitSpec,
  );
  // Touched values for this field (can be an array, string, or object).
  const fieldTouched = _.get(allTouched, accessor, []);
  // Whether the field has any touched values.
  const anyTouched = asArray(fieldTouched).some((f) => f === true);

  // Errors for this field (can be an array, string, or object).
  const fieldErrors = _.get(errors, accessor, []);
  // Remove falsy values and uniquify.
  const allErrors = _.uniq(_.compact(asArray(fieldErrors)));

  const onKeyPress = validateOnKeypress({ type: 'number', min, step });

  const { formatWithUnit, parseWithUnit } = useFormatAndParseWithUnit({
    format,
    parse,
    transformToUnit,
    transformToDefault,
  });

  const renderLabel = () => (
    <Label>
      {label}
      {displayUnit && ` (${displayUnit})`}
    </Label>
  );

  const renderInput = () => (
    <div className="w-100 overflow-hidden">
      <div className="d-flex overflow-auto">
        {Array.from({ length: N_YEARS }).map((_unused, i) => {
          const fieldName = `${accessor}[${i + indexOffset}]`;
          return (
            <Field
              name={fieldName}
              key={i} // eslint-disable-line react/no-array-index-key -- This is a static list
              parse={parseWithUnit}
              format={formatWithUnit}
            >
              {({ field, meta: { touched, error } }) => (
                <div className="d-flex flex-column align-items-center">
                  {/* Always start display indexing from 1 */}
                  <Label className="font-tiny mb-0">{i + 1}</Label>
                  <YearInput
                    {...field}
                    parse={parseWithUnit}
                    format={formatWithUnit}
                    bsSize={size === 'med' ? undefined : 'sm'}
                    type={inputType}
                    name={fieldName}
                    onKeyPress={onKeyPress}
                    invalid={
                      !!(touched && typeof fieldErrors !== 'string' && error)
                    }
                    min={min}
                    step={step}
                    {...props}
                  />
                </div>
              )}
            </Field>
          );
        })}
      </div>
    </div>
  );

  const renderError = () =>
    // Show error messages for all of the fields
    anyTouched &&
    allErrors.length > 0 && (
      <FormFeedback className="d-block">{allErrors.join(', ')}</FormFeedback>
    );

  return typeof children === 'function' ? (
    children({ renderInput, renderError, renderLabel })
  ) : (
    <FormGroup>
      {renderLabel()}
      {renderInput()}
      {renderError()}
    </FormGroup>
  );
};

export const TemplateForm = styled((props) => (
  <Form
    onKeyDown={(e) => {
      if (e.code === 'Enter' && e.target.tagName !== 'TEXTAREA') {
        // Prevent Enter keypresses from submitting the form, but also allow Enter keypresses within textarea elements.
        e.preventDefault();
      }
    }}
    {...props}
  />
))`
  padding: ${({ $removePadding }) =>
    $removePadding ? '0' : 'var(--content-y-padding) var(--cell-padding)'};
  overflow-y: scroll;
  input[type='string']${YearInput} {
    width: 6rem;
    padding: 0.2rem;
  }
  input[type='number']${YearInput} {
    width: 2rem;
    padding: 0.2rem;
  }
  .form-check-input {
    margin: 0.5rem auto;
    position: static;
    display: block;
  }
  .form-group {
    margin-right: 0.5rem;
  }
`;

export const NameInput = styled(({ name, ...props }) => (
  <Field name={name}>
    {({ field, meta: { touched, error } }) => (
      <>
        <Input
          {...field}
          value={field.value || ''}
          bsSize="lg"
          invalid={!!(touched && error)}
          type="text"
          {...props}
        />
        {touched && error && (
          <FormFeedback className="d-block">{error}</FormFeedback>
        )}
      </>
    )}
  </Field>
))`
  height: auto;
  padding: 0;
  border: none;
  border-bottom: 2px solid rgba(33, 33, 33, 0.2);
  border-radius: 0;
  &:focus {
    box-shadow: none !important;
    border-color: rgba(33, 33, 33, 0.6);
  }
  &:disabled {
    border: none;
    background-color: transparent;
  }
`;

export const DropdownButtonField = ({
  name,
  options,
  showOverflow,
  onChange = _.noop,
  ...props
}) => {
  const optionsMap = useMemo(() => _.keyBy(options, 'value'), [options]);
  const [, meta, helpers] = useField(name);
  const { value } = meta;
  const { setValue } = helpers;

  return (
    <DropdownButton
      options={options}
      selectedOption={optionsMap[value]?.name}
      setOption={({ value: newValue }) => {
        setValue(newValue);
        onChange(newValue);
      }}
      showOverflow={showOverflow}
      {...props}
    />
  );
};

export const OptionalSection = ({
  forceShown,
  children,
  buttonContent,
  className,
  sectionKey,
}) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    setShown(false);
  }, [sectionKey]);
  return forceShown || shown ? (
    children
  ) : (
    <Button
      className={`p-0 text-primary d-block ${className}`}
      color="link"
      size="sm"
      onClick={() => setShown(true)}
    >
      {buttonContent}
    </Button>
  );
};

export const Message = styled.div`
  padding: var(--content-y-padding) var(--cell-padding);
`;

const UNIT_FONT_SIZE = '0.7rem';

export const UnitLabel = styled(Label)`
  font-size: ${UNIT_FONT_SIZE};
  color: var(--text-muted-color);
  top: 50%;
  transform: translateY(-50%);
  right: ${({ $right }) => $right || '10px'};
  position: absolute;
  margin: auto 0;
  ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth};`};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PrefixLabel = styled(Label)`
  color: var(--text-muted-color);
  top: 50%;
  transform: translateY(-50%);
  left: 10px;
  position: absolute;
  margin: auto 0;
  max-width: 0.5rem;
  overflow: hidden;
`;

export const UnitInputWrapper = styled.div`
  position: relative;
  width: 100%;
  ${({ type, $maxWidth }) =>
    $maxWidth
      ? `max-width: ${$maxWidth};`
      : type === 'number' && 'max-width: 100px;'}
  input {
    max-width: 100%;
  }
`;

// An input that displays currency and units. It uses a FormattedInput which
// is uncontrolled. An onChange handler and a defaultValue should be passed in
// for this to work properly.
export const UnitInput = styled(
  ({ unit, currency, type, maxWidth, maxUnitWidth, ...props }) => {
    const [unitWidth, setUnit] = useValueWidth(unit, {
      fontSize: UNIT_FONT_SIZE,
      maxWidth: maxUnitWidth,
    });
    useEffect(() => {
      setUnit(unit);
    }, [setUnit, unit]);
    return (
      <UnitInputWrapper type={type} $maxWidth={maxWidth}>
        <FormattedInput
          type={type}
          {...props}
          style={{ paddingRight: `calc(${unitWidth}px + 10px)` }}
        />
        {currency && <PrefixLabel>$</PrefixLabel>}
        {unit && (
          <UnitLabel
            title={typeof unit === 'string' && unit}
            $maxWidth={maxUnitWidth}
          >
            {unit}
          </UnitLabel>
        )}
      </UnitInputWrapper>
    );
  },
)`
  &&& {
    ${({ currency }) => currency && 'padding-left: 1.1rem;'}
    ${({ $showSpinner }) =>
      !$showSpinner &&
      `
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        &[type='number'] {
          -moz-appearance: textfield;
        }
      `}
  }
`;

export const ValidatedInput = ({
  changeOnBlur = false, // If true, only update the formik state on blur
  // Providing a function for children allows you to render
  // the field with a custom layout. children will be called with the props:
  // { formGroupClassName, renderInput, renderError, renderLabel }
  children,
  currency,
  formGroupClassName, // className for the FormGroup
  format, // Must be memoized
  label,
  labelClassName, // className for the Label
  min,
  name,
  parse, // Must be memoized
  step,
  size,
  type,
  unit,
  unitPrecision, // Number of decimal places to round to on unit conversion
  unitSpec,
  wide, // If true, display wide input
  ...props
}) => {
  const {
    transformToUnit,
    transformToDefault,
    displayUnit: unitFromSpec,
  } = useUnits(unitSpec, unitPrecision);
  const { formatWithUnit, parseWithUnit } = useFormatAndParseWithUnit({
    // Show currency with 2 decimal places by default if a format function is not provided
    format: format || (currency ? numberAsRoundedDecimal : identity),
    parse,
    transformToUnit,
    transformToDefault,
  });
  const displayUnit = unit !== undefined ? unit : unitFromSpec;
  const onKeyPress = validateOnKeypress({ type, min, step });

  return (
    <Field name={name} parse={parseWithUnit} format={formatWithUnit}>
      {({ field, meta: { touched, error } }) => {
        const onBlur = changeOnBlur
          ? (e) => {
              field.onBlur(e);
              field.onChange(e);
            }
          : field.onBlur;
        const onChange = changeOnBlur ? noop : field.onChange;
        const renderLabel = () =>
          label && <Label className={labelClassName}>{label}</Label>;
        const renderInput = () => (
          <UnitInput
            {...field}
            parse={parseWithUnit}
            format={formatWithUnit}
            onBlur={onBlur}
            onChange={onChange}
            onKeyPress={onKeyPress}
            bsSize={size === 'med' ? undefined : 'sm'}
            invalid={!!(touched && error)}
            type={type}
            min={min}
            step={step}
            currency={currency}
            unit={displayUnit}
            {...props}
          />
        );
        const renderError = () =>
          touched &&
          error && <FormFeedback className="d-block">{error}</FormFeedback>;

        const FormGroupComponent = wide ? WideFormGroup : FormGroup;

        return typeof children === 'function' ? (
          children({
            formGroupClassName,
            renderInput,
            renderError,
            renderLabel,
          })
        ) : (
          <FormGroupComponent className={formGroupClassName}>
            {renderLabel()}
            {renderInput()}
            {renderError()}
          </FormGroupComponent>
        );
      }}
    </Field>
  );
};

export const InlineValidatedInput = ({ formGroupClassName, ...props }) => (
  <ValidatedInput {...props}>
    {({ renderLabel, renderInput, renderError }) => (
      <FormGroup className={`${formGroupClassName} d-flex m-0`}>
        {renderLabel()}
        <div>
          {renderInput()}
          {renderError()}
        </div>
      </FormGroup>
    )}
  </ValidatedInput>
);

export const ControlledDeleteButton = ({
  onDelete,
  deleting,
  setDeleting,
  buttonText = 'Delete',
  tooltipContent,
  confirmDeleteButtonProps,
  ...props
}) => {
  const buttonId = useMemo(() => _.uniqueId('delete-button-'), []);

  if (deleting)
    return (
      <div className="d-inline-flex align-items-center">
        <span className="mr-2 font-weight-bold">Are you sure?</span>
        <Button className="mr-2" onClick={() => setDeleting(false)} {...props}>
          Cancel
        </Button>
        <Button
          color="danger"
          onClick={onDelete}
          {...props}
          {...confirmDeleteButtonProps}
        >
          Delete
        </Button>
      </div>
    );
  return (
    <>
      <Button
        color="danger"
        outline
        onClick={() => setDeleting(true)}
        id={buttonId}
        {...props}
      >
        {buttonText}
      </Button>
      {tooltipContent && (
        <UncontrolledWhiteTooltip
          placement="top"
          target={buttonId}
          modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
          className="p-3"
          autohide={false}
        >
          {tooltipContent}
        </UncontrolledWhiteTooltip>
      )}
    </>
  );
};

export const DeleteButton = ({ ...props }) => {
  const [deleting, setDeleting] = useState(false);
  return (
    <ControlledDeleteButton
      {...props}
      deleting={deleting}
      setDeleting={setDeleting}
    />
  );
};

export const SubmitButton = ({ disabled, loading, children, ...props }) => {
  const { isValid, dirty, isSubmitting, errors } = useFormikContext();
  const onClick = () => {
    // For debugging errors that aren't displayed.
    if (!isValid) console.info(errors);
  };
  return (
    <Button
      onClick={onClick}
      color="primary"
      type="submit"
      disabled={disabled || !dirty || isSubmitting || loading}
      outline={!isValid}
      {...props}
    >
      {children || (isSubmitting || loading ? 'Saving... ' : 'Save')}
    </Button>
  );
};

export const TinyInput = styled(UnitInput)`
  font-size: 0.75rem;
  height: 1.3rem;
  padding: 0.25rem;
  & + ${UnitLabel} {
    padding: 3px 0;
  }
`;

export const ResourceSelectionFormGroup = styled(FormGroup)`
  overflow-x: scroll;
  padding-bottom: 10px; /* Allow some room for the scrollbar */
  .rbt-input {
    width: 120px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .btn-group .btn {
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  .rbt-menu {
    font-size: 0.75rem;
  }
`;
